import * as React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { UseFormReturn } from 'react-hook-form';
import { pickBy } from 'lodash';

import { useMutation } from '@tanstack/react-query';
import { useAccountsService } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { activeAddressIdAtom, activeRestaurantAtom, googleAddresInputValueAtom } from './google-address-input-result';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import { restaurantInformationDataAtom, useRestaurantOnboardingData } from './use-restaurant-onboarding-data-query';
import { binaryPicturesAtom } from './restaurant-onboarding-image-gallery';
import {
  activeRestaurantOnboardingStateAtom,
  restaurantOnboardingFormDefaultValues,
  restaurantOnboardingModalVisibleAtom,
} from '.';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';

// hooks
export const useRestaurantOnboardingResetActions = () => {
  const setGoogleInputAddressValue = useSetAtom(googleAddresInputValueAtom);
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const restaurantOnboardingModalVisibleToggler = useSetAtom(restaurantOnboardingModalVisibleAtom);
  const setBinaryPictures = useSetAtom(binaryPicturesAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);
  const setActiveGoogleAddressId = useSetAtom(activeAddressIdAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);

  const resetState = React.useCallback(() => {
    setTimeout(() => {
      setActiveRestaurantName('');
      setActiveGoogleAddressId('');
      setGoogleInputAddressValue('');
      setActiveUI('select_restaurant_from_google');
      setBinaryPictures([]);
      setRestaurantInformationData({});
    }, 250); // for delay before closing the modal
  }, [
    setActiveGoogleAddressId,
    setActiveRestaurantName,
    setActiveUI,
    setBinaryPictures,
    setGoogleInputAddressValue,
    setRestaurantInformationData,
  ]);

  const handleModalClose = React.useCallback(() => {
    restaurantOnboardingModalVisibleToggler(false);
    resetState();
  }, [resetState, restaurantOnboardingModalVisibleToggler]);

  return { resetState, handleModalClose };
};

export const useFormatRestaurantOnboardingData = () => {
  const businessAddress = useAtomValue(activeRestaurantAtom);
  const binaryPictures = useAtomValue(binaryPicturesAtom);
  const isGettingStartedPage = useGettingStartedPage();
  const formatFormData = React.useCallback(
    (data: RestaurantOnboardingPayload) => {
      const { terms_and_conditions, privacy_policy, number_of_staffs, pictures, ...payloadData } = data;
      let payload = {};
      const additionalRestaurantData = {
        ...(pictures?.length ? { pictures } : {}),
        ...(number_of_staffs
          ? {
              staff_size_floor: Math.round(+number_of_staffs / 2),
              staff_size_kitchen: Math.round(+number_of_staffs / 2),
            }
          : {}),
        ...(binaryPictures?.length ? { binary_pictures: binaryPictures.map((file) => file.file) } : {}),
        business_address: businessAddress,
        show_details: true,
      };
      const orgContactInfoArray = ['name', 'surname', 'email', 'phone_number'];
      if (isGettingStartedPage) {
        const contactInformation = pickBy(payloadData, (_, key) => orgContactInfoArray.includes(key));
        const restaurantInfo = pickBy(payloadData, (_, key) => !orgContactInfoArray.includes(key));
        payload = {
          employer_user_contacts: {
            ...contactInformation,
          },
          restaurants: {
            ...restaurantInfo,
            ...additionalRestaurantData,
          },
        };
      } else {
        payload = {
          ...payloadData,
          ...additionalRestaurantData,
        };
      }
      return payload;
    },
    [binaryPictures, businessAddress, isGettingStartedPage]
  );

  return formatFormData;
};
// Custom Hook for Form reset Logic
export const useRestaurantOnboardingFormReset = ({
  methods,
}: {
  methods?: UseFormReturn<RestaurantOnboardingPayload, any>;
}) => {
  const { user } = useAuth();
  const restaurantOnboardingData = useRestaurantOnboardingData();
  const restaurantOnboardingModalVisible = useAtomValue(restaurantOnboardingModalVisibleAtom);
  const { resetState } = useRestaurantOnboardingResetActions();

  React.useEffect(() => {
    resetState();
  }, [resetState, restaurantOnboardingModalVisible]);

  React.useEffect(() => {
    methods.reset({
      ...restaurantOnboardingFormDefaultValues,
    });
  }, [methods]);

  React.useEffect(() => {
    setTimeout(() => {
      if (restaurantOnboardingData)
        methods.reset({
          ...restaurantOnboardingData,
          ...(user?.email
            ? {
                email: user?.email || '',
                firstName: user?.name || '',
                lastName: user?.surname || '',
                phone_number: user?.phone_number || '',
              }
            : {}),
          number_of_staffs: (
            (restaurantOnboardingData.staff_size_floor || 0) + (restaurantOnboardingData.staff_size_kitchen || 0)
          ).toString(),
          email: user?.email || '',
        });
    }, 250);
  }, [restaurantOnboardingData, methods, user?.email, user?.name, user?.surname, user?.phone_number]);
};

export const useGettingStartedPage = () => {
  const { pathname } = useV2Router();
  return pathname.includes('/getting-started');
};

export const useAccountAlreadyExistOrNotMutation = () => {
  const setActiveState = useSetAtom(activeRestaurantOnboardingStateAtom);
  const accountServices = useAccountsService();
  const { trackPHEvent } = useAnalytics();

  const queryData = useMutation({
    mutationFn: async (params: { email?: string; phone_number?: string }) =>
      (await accountServices.checkUserExistence(params)).data,
    // retry: 1,
    onSuccess() {
      setActiveState('otp_verification');
      trackPHEvent('freemium_employer_confirms_user_data', {});
    },
    onError(error) {
      if (error?.['code'] === 409) setActiveState('contact_already_exists');
    },
  });
  return queryData;
};
